import { useEffect, useRef } from "react";
import {
  ensureTradePortalAuthFetch,
  fetchTradeChatConfig,
  fetchTradePortalClientConfig,
  installTradePortalClientIntegration,
  resolveTradePortalSessionUrl,
  TRADE_PORTAL_TOKEN_URL,
  withTradeScriptVersion,
} from "../libs/tradeChatConfig";
import "../css/tradeWebManagerHost.css";

const SCRIPT_ID = "trade-portal-script";
const ROOT_ID = "trade_portal_root";

const removeTradePortalScript = () => {
  const current = document.getElementById(SCRIPT_ID);
  if (current) {
    current.remove();
  }
};

const loadPortalScript = (portalScriptUrl, tokenUrl) =>
  new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      if (window.TradePortal) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Không tải được trade_portal.js")),
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = withTradeScriptVersion(portalScriptUrl);
    script.async = true;
    script.setAttribute("data-token-url", tokenUrl);
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Không tải được trade_portal.js"));
    document.body.appendChild(script);
  });

const showTradeBootError = (message) => {
  const root = document.getElementById(ROOT_ID);
  if (root) {
    root.textContent = message;
  }
  console.error(message);
};

const bootServerSecurityPortal = async () => {
  const config = await fetchTradeChatConfig();
  ensureTradePortalAuthFetch();
  const tokenUrl = resolveTradePortalSessionUrl(
    config.portalSessionPath || TRADE_PORTAL_TOKEN_URL
  );
  await loadPortalScript(config.portalScriptUrl, tokenUrl);
};

const bootClientSecurityPortal = async () => {
  const portalConfig = await fetchTradePortalClientConfig();
  installTradePortalClientIntegration({
    publicKey: portalConfig.publicKey,
    portalDomain: portalConfig.portalDomain,
    tokenUrl: portalConfig.tokenUrl,
  });
  await loadPortalScript(
    portalConfig.portalScriptUrl,
    portalConfig.tokenUrl || TRADE_PORTAL_TOKEN_URL
  );
};

/**
 * Nhúng trade_portal.js trên trang có #trade_portal_root.
 * Server security: proxy session qua ZTO backend.
 * Client security: Portal key + portal domain từ API, tích hợp giống portal.html.
 */
const TradePortalClient = () => {
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    let cancelled = false;

    const tearDown = () => {
      cancelled = true;
      removeTradePortalScript();
    };

    (async () => {
      if (!document.getElementById(ROOT_ID)) {
        return;
      }
      try {
        const modeConfig = await fetchTradeChatConfig();
        if (cancelled || requestId !== requestIdRef.current) return;

        if (modeConfig.portalSecurityMode === "client") {
          await bootClientSecurityPortal();
        } else {
          await bootServerSecurityPortal();
        }

        if (cancelled || requestId !== requestIdRef.current) return;
      } catch (err) {
        if (!cancelled) {
          showTradeBootError("Lỗi: " + (err?.message || err));
        }
      }
    })();

    return () => tearDown();
  }, []);

  return null;
};

export default TradePortalClient;
